import { useSetAtom } from 'jotai';
import { ReactNode, useState } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';

import { DrawerProps, FormDrawer } from '@/components';
import { cn } from '@/lib/cn';

import { useLogger } from '../lib/log';
import { isNotepadOpenAtom } from '../model';
import { MinimizedDrawer } from './MinimizedDrawer';

type MinimalizableFormDrawerProps<TFieldValues extends FieldValues> = {
    form: UseFormReturn<TFieldValues>;
    minimizedTitle: string;
    minimizedSubtitle?: string;
    children: ReactNode;
    minimizedDrawerType:
        | 'journal'
        | 'message'
        | 'clinicalMessage'
        | 'referral'
        | 'internal-task'
        | 'household-note';
} & Omit<DrawerProps, 'onMinimize'>;

export const MinimalizableFormDrawer = <TFieldValues extends FieldValues>({
    form,
    minimizedTitle,
    minimizedSubtitle,
    children,
    minimizedDrawerType,
    onClose,
    className,
    ...drawerProps
}: MinimalizableFormDrawerProps<TFieldValues>) => {
    const [minimized, setMinimized] = useState(false);
    const log = useLogger();
    const openNotepad = useSetAtom(isNotepadOpenAtom);

    const handleMinimize = () => {
        log(`Minimized Drawer ${minimizedDrawerType}`);
        setMinimized(true);
    };

    const handleMaximize = () => {
        log(`Maximized Drawer ${minimizedDrawerType}`);
        setMinimized(false);
    };

    const handleClose = () => {
        setMinimized(false);
        onClose?.();
    };

    const handleMinimizedClose = () => {
        setMinimized(false);
        handleClose();
    };

    return (
        <>
            <MinimizedDrawer
                minimized={minimized}
                title={minimizedTitle}
                subtitle={minimizedSubtitle}
                onClose={handleMinimizedClose}
                onMaximize={handleMaximize}
            />
            <FormDrawer
                form={form}
                className={cn(minimized && 'hidden', className)}
                onClose={handleClose}
                onMinimize={handleMinimize}
                onOpenNotepad={() => openNotepad(true)}
                {...drawerProps}
            >
                {children}
            </FormDrawer>
        </>
    );
};
